import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { MarkdownContent } from '../components/MarkdownContent';
import { MediaViewer, ImageCard, VideoCard } from '../components/MediaViewer';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  ShoppingBag, 
  Image as ImageIcon,
  Video,
  FileText,
  Loader2,
  ExternalLink,
  Download,
  Star,
  MessageSquare,
  ChevronRight
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

export default function ShopifyProductDetailPage() {
  const { ecomId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [shopify, setShopify] = useState(null);
  const [shopifyImages, setShopifyImages] = useState(null);
  const [shopifySections, setShopifySections] = useState(null);
  const [winning, setWinning] = useState(null);
  const [winningImages, setWinningImages] = useState(null);
  const [ugcFullVideos, setUgcFullVideos] = useState(null);
  const [ugcShorts, setUgcShorts] = useState(null);
  
  // Media viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentMedia, setCurrentMedia] = useState({ url: '', type: 'image', title: '' });
  const [allMedia, setAllMedia] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const results = await Promise.all([
        supabase.from('products').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('shopify').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('shopify_product_images').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('shopify_sections').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('winning_product_details').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('winning_product_images').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('ugc_full_videos').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('ugc_shorts').select('*').eq('ecom_id', ecomId).single(),
      ]);

      setProduct(results[0].data);
      setShopify(results[1].data);
      setShopifyImages(results[2].data);
      setShopifySections(results[3].data);
      setWinning(results[4].data);
      setWinningImages(results[5].data);
      setUgcFullVideos(results[6].data);
      setUgcShorts(results[7].data);
    } catch (error) {
      console.error('Error fetching Shopify product:', error);
      toast.error('Failed to load Shopify product details');
    } finally {
      setLoading(false);
    }
  }, [ecomId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const displayName = shopify?.product_name || winning?.product_name || ecomId;

  const openImageViewer = (url, title, mediaList = [], index = 0) => {
    setCurrentMedia({ url, type: 'image', title });
    setAllMedia(mediaList);
    setCurrentIndex(index);
    setViewerOpen(true);
  };

  const openVideoViewer = (url, title, mediaList = [], index = 0) => {
    setCurrentMedia({ url, type: 'video', title });
    setAllMedia(mediaList);
    setCurrentIndex(index);
    setViewerOpen(true);
  };

  const handleNavigate = (newIndex) => {
    if (allMedia[newIndex]) {
      setCurrentIndex(newIndex);
      setCurrentMedia({
        url: allMedia[newIndex].url,
        type: allMedia[newIndex].type || 'image',
        title: allMedia[newIndex].title
      });
    }
  };

  // Collect all images
  const getAllImages = () => {
    const images = [];
    // Hero images from winning
    for (let i = 1; i <= 5; i++) {
      if (winningImages?.[`image${i}`]) {
        images.push({ url: winningImages[`image${i}`], title: `Hero Image ${i}`, type: 'image' });
      }
    }
    // Shopify images
    for (let i = 1; i <= 6; i++) {
      if (shopifyImages?.[`image${i}`]) {
        images.push({ url: shopifyImages[`image${i}`], title: `Shopify Image ${i}`, type: 'image' });
      }
    }
    return images;
  };

  // Collect all videos
  const getAllVideos = () => {
    const videos = [];
    for (let i = 1; i <= 4; i++) {
      if (ugcFullVideos?.[`video${i}`]) {
        videos.push({ url: ugcFullVideos[`video${i}`], title: `Full Video ${i}`, type: 'video' });
      }
    }
    for (let i = 1; i <= 4; i++) {
      if (ugcShorts?.[`video${i}`]) {
        videos.push({ url: ugcShorts[`video${i}`], title: `Short ${i}`, type: 'video' });
      }
    }
    return videos;
  };

  const handleDownloadAllImages = async () => {
    const images = getAllImages();
    toast.info(`Downloading ${images.length} images...`);
    for (let i = 0; i < images.length; i++) {
      try {
        const response = await fetch(images[i].url);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `shopify_image_${i + 1}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        await new Promise(resolve => setTimeout(resolve, 200));
      } catch (error) {
        console.error(`Failed to download image ${i + 1}`);
      }
    }
    toast.success(`Downloaded ${images.length} images`);
  };

  if (loading) {
    return (
      <Layout productName="Loading...">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!shopify) {
    return (
      <Layout>
        <div className="text-center py-16">
          <ShoppingBag className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600 mb-4">Shopify product not found</p>
          <button
            onClick={() => navigate('/shopify')}
            className="px-6 py-2 bg-cyan-500 text-white rounded-lg"
          >
            Back to Shopify
          </button>
        </div>
      </Layout>
    );
  }

  const allImages = getAllImages();
  const allVideos = getAllVideos();

  return (
    <Layout productName={displayName}>
      <div data-testid="shopify-product-detail">
        {/* Header */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
          <div className="flex flex-col gap-4">
            {/* Top row: Back + Title */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/shopify')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {allImages[0] ? (
                  <img
                    src={allImages[0].url}
                    alt={displayName}
                    className="w-10 h-10 md:w-14 md:h-14 object-cover rounded-lg border-2 border-slate-200 flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 md:w-14 md:h-14 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ShoppingBag className="w-5 h-5 md:w-6 md:h-6 text-indigo-600" />
                  </div>
                )}
                
                <div className="min-w-0 flex-1">
                  <h1 className="text-base md:text-xl font-bold text-[#1E1B4B] font-['Manrope'] truncate">
                    {displayName}
                  </h1>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <StatusBadge status={shopify.status} lane="shopify" />
                  </div>
                </div>
              </div>
            </div>

            {/* Action buttons - stack on mobile */}
            <div className="flex flex-wrap gap-2">
              {shopify?.product_page && (
                <a
                  href={shopify.product_page}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 text-sm font-medium flex-1 md:flex-none justify-center"
                  data-testid="view-on-shopify-btn"
                >
                  <ShoppingBag className="w-4 h-4" />
                  <span>View on Shopify</span>
                </a>
              )}
              {allImages.length > 0 && (
                <button
                  onClick={handleDownloadAllImages}
                  className="px-3 py-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg flex items-center gap-2 text-sm font-medium flex-1 md:flex-none justify-center"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Download</span> ({allImages.length})
                </button>
              )}
              {product?.url && (
                <a
                  href={product.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg flex items-center gap-2 text-sm flex-1 md:flex-none justify-center"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span className="hidden sm:inline">Original</span>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="bg-slate-100 p-1 w-full flex overflow-x-auto">
            <TabsTrigger value="overview" className="data-[state=active]:bg-white flex items-center gap-1 flex-1 text-xs md:text-sm">
              <ShoppingBag className="w-3 h-3 md:w-4 md:h-4" />
              <span className="truncate">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="images" className="data-[state=active]:bg-white flex items-center gap-1 flex-1 text-xs md:text-sm">
              <ImageIcon className="w-3 h-3 md:w-4 md:h-4" />
              <span className="truncate">Images</span>
            </TabsTrigger>
            <TabsTrigger value="videos" className="data-[state=active]:bg-white flex items-center gap-1 flex-1 text-xs md:text-sm">
              <Video className="w-3 h-3 md:w-4 md:h-4" />
              <span className="truncate">Videos</span>
            </TabsTrigger>
            <TabsTrigger value="sections" className="data-[state=active]:bg-white flex items-center gap-1 flex-1 text-xs md:text-sm">
              <FileText className="w-3 h-3 md:w-4 md:h-4" />
              <span className="truncate">Sections</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4 md:space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              {/* Main Image Gallery */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-6">
                <h3 className="font-semibold text-slate-700 mb-4">Product Images</h3>
                {allImages.length > 0 ? (
                  <>
                    <div 
                      className="aspect-square bg-slate-100 rounded-lg overflow-hidden mb-4 cursor-pointer"
                      onClick={() => openImageViewer(allImages[0].url, allImages[0].title, allImages, 0)}
                    >
                      <img src={allImages[0].url} alt="Main" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {allImages.slice(1, 5).map((img, i) => (
                        <ImageCard
                          key={i}
                          url={img.url}
                          label={img.title}
                          onView={() => openImageViewer(img.url, img.title, allImages, i + 1)}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="aspect-square bg-slate-100 rounded-lg flex items-center justify-center">
                    <ImageIcon className="w-16 h-16 text-slate-300" />
                  </div>
                )}}
              </div>

              {/* Product Details */}
              <div className="space-y-4">
                {/* Price */}
                {winning?.price && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                    <p className="text-sm text-emerald-600 font-medium mb-1">Selling Price</p>
                    <p className="text-4xl font-bold text-emerald-700">${winning.price}</p>
                  </div>
                )}

                {/* Description */}
                <div className="bg-white border border-slate-200 rounded-xl p-6">
                  <h4 className="font-semibold text-slate-700 mb-3">Description</h4>
                  <MarkdownContent 
                    content={shopify?.description || winning?.description} 
                    title="Product Description"
                    maxHeight={150}
                  />
                </div>

                {/* Features */}
                {(shopify?.features || winning?.features) && (
                  <div className="bg-white border border-slate-200 rounded-xl p-6">
                    <h4 className="font-semibold text-slate-700 mb-3">Features</h4>
                    <MarkdownContent 
                      content={shopify?.features || winning?.features} 
                      title="Product Features"
                      maxHeight={150}
                    />
                  </div>
                )}

                {/* How to Use */}
                {(shopify?.how_to_use || winning?.how_to_use) && (
                  <div className="bg-white border border-slate-200 rounded-xl p-6">
                    <h4 className="font-semibold text-slate-700 mb-3">How to Use</h4>
                    <MarkdownContent 
                      content={shopify?.how_to_use || winning?.how_to_use} 
                      title="How to Use"
                      maxHeight={150}
                    />
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Images Tab */}
          <TabsContent value="images" className="space-y-6">
            {/* Hero Images */}
            {winningImages && (
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-700">Hero Images</h3>
                  <StatusBadge status={winningImages.status} lane="winning" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {[1, 2, 3, 4, 5].map(i => {
                    const url = winningImages[`image${i}`];
                    const imgIndex = allImages.findIndex(img => img.url === url);
                    return (
                      <ImageCard
                        key={i}
                        url={url}
                        label={`Hero ${i}`}
                        onView={() => url && openImageViewer(url, `Hero Image ${i}`, allImages, imgIndex >= 0 ? imgIndex : 0)}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Shopify Images */}
            {shopifyImages && (
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-700">Shopify Images</h3>
                  <StatusBadge status={shopifyImages.status} lane="shopify" />
                </div>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                  {[1, 2, 3, 4, 5, 6].map(i => {
                    const url = shopifyImages[`image${i}`];
                    const imgIndex = allImages.findIndex(img => img.url === url);
                    return (
                      <ImageCard
                        key={i}
                        url={url}
                        label={`Shopify ${i}`}
                        onView={() => url && openImageViewer(url, `Shopify Image ${i}`, allImages, imgIndex >= 0 ? imgIndex : 0)}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Videos Tab */}
          <TabsContent value="videos" className="space-y-6">
            {/* Full Videos */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="font-semibold text-slate-700 mb-4">Full Videos</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map(i => {
                  const url = ugcFullVideos?.[`video${i}`];
                  const vidIndex = allVideos.findIndex(v => v.url === url);
                  return (
                    <VideoCard
                      key={i}
                      url={url}
                      label={`Full Video ${i}`}
                      onView={() => url && openVideoViewer(url, `Full Video ${i}`, allVideos, vidIndex >= 0 ? vidIndex : 0)}
                    />
                  );
                })}
              </div>
            </div>

            {/* Shorts */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="font-semibold text-slate-700 mb-4">Short Videos</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                {[1, 2, 3, 4].map(i => {
                  const url = ugcShorts?.[`video${i}`];
                  const vidIndex = allVideos.findIndex(v => v.url === url);
                  return (
                    <VideoCard
                      key={i}
                      url={url}
                      label={`Short ${i}`}
                      onView={() => url && openVideoViewer(url, `Short ${i}`, allVideos, vidIndex >= 0 ? vidIndex : 0)}
                    />
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* Sections Tab */}
          <TabsContent value="sections" className="space-y-6">
            {shopifySections ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { key: 'faq_section', label: 'FAQ Section', icon: MessageSquare },
                  { key: 'testimonials', label: 'Testimonials', icon: Star },
                  { key: 'accordion', label: 'Accordion', icon: ChevronRight },
                  { key: 'compare_section', label: 'Compare Section', icon: FileText },
                  { key: 'benefits_section', label: 'Benefits', icon: Star },
                  { key: 'claims_reviews', label: 'Claims & Reviews', icon: MessageSquare },
                  { key: 'bundles', label: 'Bundles', icon: ShoppingBag },
                  { key: 'why_product', label: 'Why This Product', icon: FileText },
                ].map(({ key, label, icon: Icon }) => (
                  <div key={key} className="bg-white border border-slate-200 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="w-4 h-4 text-slate-500" />
                      <h4 className="font-semibold text-slate-700">{label}</h4>
                    </div>
                    {shopifySections[key] ? (
                      <MarkdownContent 
                        content={shopifySections[key]} 
                        title={label}
                        maxHeight={150}
                      />
                    ) : (
                      <p className="text-sm text-slate-400 italic">Not generated</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-500">No page sections generated yet</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Media Viewer */}
        <MediaViewer
          open={viewerOpen}
          onOpenChange={setViewerOpen}
          mediaUrl={currentMedia.url}
          mediaType={currentMedia.type}
          title={currentMedia.title}
          allMedia={allMedia}
          currentIndex={currentIndex}
          onNavigate={handleNavigate}
        />
      </div>
    </Layout>
  );
}
